import { Box, CircularProgress, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faVideo } from "@fortawesome/free-solid-svg-icons";
import moment from "moment";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  getAppointmentStatus,
  APPOINTMENT_STATUS
} from "../../../hooks/useAppointments";
import { Appointment } from "../../../state/appointments";
import { typography } from "../../../utils/typography";
import { semanticColors } from "../../../utils/colors";
import { StatusCell } from "./AppointmentsTable";

interface Props {
  /** Appointment to show, or null when the dialog is closed. */
  appointment: Appointment | null;
  /** Current doctor's display name (already prefixed with Dr./Dra.). */
  doctorName: string;
  /** Shareable room link for a scheduled appointment; "" when unavailable. */
  inviteLink: string;
  /** Twilio room is being created (Start button shows a spinner). */
  starting: boolean;
  onClose: () => void;
  onStart: (appt: Appointment) => void;
}

const overlaySx = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 9,
  bgcolor: `${semanticColors.surface.inverse}2E`,
  backdropFilter: "blur(8px)",
  WebkitBackdropFilter: "blur(8px)",
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "center",
  overflowY: "auto",
  pt: "148px",
  px: { xs: "16px", sm: "24px" },
  pb: "48px"
};

const cardSx = {
  width: "100%",
  maxWidth: "440px",
  bgcolor: semanticColors.surface.canvas,
  borderRadius: "24px",
  boxShadow: `0 8px 32px ${semanticColors.surface.inverse}33`,
  p: "24px",
  position: "relative",
  animation: "fadeScaleIn 0.25s ease",
  "@keyframes fadeScaleIn": {
    from: { opacity: 0, transform: "scale(0.96) translateY(10px)" },
    to: { opacity: 1, transform: "scale(1) translateY(0)" }
  }
};

const closeBtnSx = {
  position: "absolute",
  top: 12,
  right: 12,
  color: semanticColors.text.secondary
};

const titleSx = {
  ...typography.heading.s,
  color: semanticColors.text.primary,
  mb: 0.5,
  pr: "32px"
};

const rowsSx = {
  mt: 2,
  display: "flex",
  flexDirection: "column",
  gap: "12px"
};

const rowSx = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "baseline",
  gap: 2
};

const rowLabelSx = {
  ...typography.body.m,
  color: semanticColors.text.secondary,
  flexShrink: 0
};

const rowValueSx = {
  ...typography.body.mBold,
  color: semanticColors.text.primary,
  textAlign: "right" as const,
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis"
};

const actionsSx = {
  mt: 3,
  display: "flex",
  gap: 1.5
};

const copyBtnSx = {
  flex: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  height: "44px",
  borderRadius: "40px",
  border: `1.5px solid ${semanticColors.action.primary}`,
  bgcolor: "transparent",
  cursor: "pointer",
  color: semanticColors.action.primary,
  "&:hover": { bgcolor: semanticColors.highlight.low }
};

const startBtnSx = (starting: boolean) => ({
  flex: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  height: "44px",
  borderRadius: "40px",
  border: "none",
  background: semanticColors.gradient.surface,
  cursor: starting ? "not-allowed" : "pointer",
  opacity: starting ? 0.7 : 1
});

const btnLabelSx = (color: string) => ({ ...typography.body.mBold, color });

export default function AppointmentDetailsDialog({
  appointment,
  doctorName,
  inviteLink,
  starting,
  onClose,
  onStart
}: Props) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  if (!appointment) return null;

  const isScheduled =
    getAppointmentStatus(appointment) === APPOINTMENT_STATUS.Scheduled;

  const handleCopy = () => {
    if (!inviteLink) return;
    navigator.clipboard.writeText(inviteLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const rows: Array<{ label: string; value: string }> = [
    {
      label: t("videoConsultations.tableStartTime"),
      value: moment(appointment.start).format(t("common.datetimeFormat"))
    },
    {
      label: t("videoConsultations.tableDuration"),
      value: t("videoConsultations.durationValue", {
        count: appointment.durationMinutes
      })
    },
    { label: t("videoDialog.patient"), value: appointment.patientName },
    { label: t("videoConsultations.doctor"), value: doctorName }
  ];

  return (
    <Box sx={overlaySx} onClick={onClose}>
      <Box sx={cardSx} onClick={e => e.stopPropagation()}>
        <IconButton size="small" onClick={onClose} sx={closeBtnSx}>
          <CloseIcon fontSize="small" />
        </IconButton>

        <Typography sx={titleSx}>
          {t("videoConsultations.appointmentDetails")}
        </Typography>
        <StatusCell appt={appointment} />

        <Box sx={rowsSx}>
          {rows.map(({ label, value }) => (
            <Box key={label} sx={rowSx}>
              <Typography sx={rowLabelSx}>{label}</Typography>
              <Typography sx={rowValueSx} noWrap>
                {value}
              </Typography>
            </Box>
          ))}
        </Box>

        {isScheduled && (
          <Box sx={actionsSx}>
            {inviteLink && (
              <Box component="button" onClick={handleCopy} sx={copyBtnSx}>
                <ContentCopyIcon sx={{ fontSize: 18 }} />
                <Typography sx={btnLabelSx(semanticColors.action.primary)}>
                  {copied
                    ? t("videoConsultations.copied")
                    : t("videoConsultations.copyLink")}
                </Typography>
              </Box>
            )}
            <Box
              component="button"
              onClick={() => !starting && onStart(appointment)}
              disabled={starting}
              sx={startBtnSx(starting)}
            >
              {starting ? (
                <CircularProgress
                  size={16}
                  sx={{ color: semanticColors.text.inverse }}
                />
              ) : (
                <FontAwesomeIcon
                  icon={faVideo}
                  style={{ fontSize: 16, color: semanticColors.text.inverse }}
                />
              )}
              <Typography sx={btnLabelSx(semanticColors.text.inverse)}>
                {t("videoConsultations.start")}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}
